import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Toast } from '../components/ui/Toast';

interface ToastOptions {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning' | 'bug-closed' | 'feature-closed';
  duration?: number;
  onClick?: () => void;
  onDismiss?: () => void;
  onEasterEgg?: () => void;
  copyPayload?: string;
  persistent?: boolean;
}

interface ToastContextValue {
  /** Returns the toast's id so callers can close it later with dismissToast. */
  showToast: (options: ToastOptions) => number;
  /** Close a toast programmatically -- for persistent toasts whose cause is
   *  resolved elsewhere (e.g. a driver's work request gets acknowledged). */
  dismissToast: (id: number) => void;
}

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastState extends ToastOptions {
  id: number;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const nextIdRef = useRef(1);

  const showToast = useCallback((options: ToastOptions) => {
    const id = nextIdRef.current++;
    setToasts(prev => [...prev, { ...options, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => {
      const toast = prev.find(t => t.id === id);
      if (toast?.onDismiss) toast.onDismiss();
      return prev.filter(t => t.id !== id);
    });
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast: removeToast }}>
      {children}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClick={toast.onClick}
          onClose={() => removeToast(toast.id)}
          onEasterEgg={toast.onEasterEgg}
          index={index}
          copyPayload={toast.copyPayload}
          persistent={toast.persistent}
        />
      ))}
    </ToastContext.Provider>
  );
};
